import { PointerDragBehavior } from '@babylonjs/core/Behaviors/Meshes/pointerDragBehavior';
import { PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { BoxBuilder } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CylinderBuilder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Observable, Observer } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Nullable } from '@babylonjs/core/types';
import { KbGizmo } from './gizmo';

/**
 * Single axis scale gizmo
 */
export class KbAxisScaleGizmo extends KbGizmo {
    private _coloredMaterial: StandardMaterial;
    /**
     * Drag behavior responsible for the gizmos dragging interactions
     */
    public dragBehavior: PointerDragBehavior;
    private _pointerObserver: Nullable<Observer<PointerInfo>> = null;
    /**
     * Scale distance in babylon units that the gizmo will snap to when dragged (Default: 0)
     */
    public snapDistance = 0;
    /**
     * Event that fires each time the gizmo snaps to a new location.
     * * snapDistance is the the change in distance
     */
    public onSnapObservable = new Observable<{ snapDistance: number }>();
    public updateObservable = new Observable<{ delta: Vector3 }>();
    /**
     * If the scaling operation should be done on all axis (default: false)
     */
    public uniformScaling = false;
    /**
     * Creates an AxisScaleGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     * @param dragAxis The axis which the gizmo will be able to scale on
     * @param color The color of the gizmo
     */
    constructor(
        dragAxis: Vector3,
        color: Color3 = Color3.Gray(),
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer
    ) {
        super(gizmoLayer);

        // Create Material
        this._coloredMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        this._coloredMaterial.disableLighting = true;
        this._coloredMaterial.emissiveColor = color;
        // this._coloredMaterial.diffuseColor = color;
        // this._coloredMaterial.specularColor = color.subtract(new Color3(0.1, 0.1, 0.1));

        const hoverMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        hoverMaterial.disableLighting = true;
        hoverMaterial.emissiveColor = color.add(new Color3(0.3, 0.3, 0.3));
        // hoverMaterial.diffuseColor = color.add(new Color3(0.3, 0.3, 0.3));

        // Build mesh on root node
        const arrow = new AbstractMesh('', gizmoLayer.utilityLayerScene);
        const arrowMesh = BoxBuilder.CreateBox('yPosMesh', { size: 0.4 }, gizmoLayer.utilityLayerScene);
        const arrowTail = CylinderBuilder.CreateCylinder(
            'cylinder',
            { diameterTop: 0.005, height: 0.275, diameterBottom: 0.005, tessellation: 96 },
            gizmoLayer.utilityLayerScene
        );
        arrowTail.material = this._coloredMaterial;
        arrow.addChild(arrowMesh);
        arrow.addChild(arrowTail);

        // Position arrow pointing in its drag axis
        arrowMesh.scaling.scaleInPlace(0.1);
        arrowMesh.material = this._coloredMaterial;
        arrowMesh.rotation.x = Math.PI / 2;
        arrowMesh.position.z += 0.3;
        arrowTail.position.z += 0.275 / 2;
        arrowTail.rotation.x = Math.PI / 2;
        arrow.lookAt(this._rootMesh.position.add(dragAxis));
        this._rootMesh.addChild(arrow);
        arrow.scaling.scaleInPlace(1 / 3);

        // Add drag behavior to handle events when the gizmo is dragged
        this.dragBehavior = new PointerDragBehavior({ dragAxis: dragAxis });
        this.dragBehavior.moveAttached = false;
        this._rootMesh.addBehavior(this.dragBehavior);

        let currentSnapDragDistance = 0;
        const tmpVector = new Vector3();
        const tmpSnapEvent = { snapDistance: 0 };
        this.dragBehavior.onDragObservable.add(event => {
            if (this.attachedMesh) {
                // Drag strength is modified by the scale of the gizmo (eg. for small objects like boombox the strength will be increased to match the behavior of larger objects)
                const dragStrength = event.dragDistance * ((this.scaleRatio * 8) / this._rootMesh.scaling.length());

                // Snapping logic
                let snapped = false;
                let dragSteps = 0;
                if (this.uniformScaling) {
                    this.attachedMesh.scaling.normalizeToRef(tmpVector);
                    if (tmpVector.y < 0) {
                        tmpVector.scaleInPlace(-1);
                    }
                } else {
                    tmpVector.copyFrom(dragAxis);
                }
                if (this.snapDistance == 0) {
                    tmpVector.scaleToRef(dragStrength, tmpVector);
                } else {
                    currentSnapDragDistance += dragStrength;
                    if (Math.abs(currentSnapDragDistance) > this.snapDistance) {
                        dragSteps = Math.floor(Math.abs(currentSnapDragDistance) / this.snapDistance);
                        if (currentSnapDragDistance < 0) {
                            dragSteps *= -1;
                        }
                        currentSnapDragDistance = currentSnapDragDistance % this.snapDistance;
                        tmpVector.scaleToRef(this.snapDistance * dragSteps, tmpVector);
                        snapped = true;
                    } else {
                        tmpVector.scaleInPlace(0);
                    }
                }

                this.attachedMesh.scaling.addInPlace(tmpVector);
                this.updateObservable.notifyObservers({ delta: tmpVector });

                if (snapped) {
                    tmpSnapEvent.snapDistance = this.snapDistance * dragSteps;
                    this.onSnapObservable.notifyObservers(tmpSnapEvent);
                }
            }
        });

        this._pointerObserver = gizmoLayer.utilityLayerScene.onPointerObservable.add(pointerInfo => {
            if (this._customMeshSet) {
                return;
            }
            const isHovered =
                pointerInfo.pickInfo &&
                this._rootMesh.getChildMeshes().indexOf(<Mesh>pointerInfo.pickInfo.pickedMesh) != -1;
            const material = isHovered ? hoverMaterial : this._coloredMaterial;
            this._rootMesh.getChildMeshes().forEach(m => {
                m.material = material;
                if ((<LinesMesh>m).color) {
                    (<LinesMesh>m).color = material.diffuseColor;
                }
            });
        });

        // var light = gizmoLayer._getSharedGizmoLight();
        // light.includedOnlyMeshes = light.includedOnlyMeshes.concat(this._rootMesh.getChildMeshes());
    }

    protected _attachedMeshChanged(value: Nullable<AbstractMesh>) {
        if (this.dragBehavior) {
            this.dragBehavior.enabled = value ? true : false;
        }
    }

    /**
     * Disposes of the gizmo
     */
    public dispose() {
        this.onSnapObservable.clear();
        this.gizmoLayer.utilityLayerScene.onPointerObservable.remove(this._pointerObserver);
        this.dragBehavior.detach();
        super.dispose();
    }

    /**
     * Disposes and replaces the current meshes in the gizmo with the specified mesh
     * @param mesh The mesh to replace the default mesh of the gizmo
     * @param useGizmoMaterial If the gizmo's default material should be used (default: false)
     */
    public setCustomMesh(mesh: Mesh, useGizmoMaterial = false) {
        super.setCustomMesh(mesh);
        if (useGizmoMaterial) {
            this._rootMesh.getChildMeshes().forEach(m => {
                m.material = this._coloredMaterial;
                if ((<LinesMesh>m).color) {
                    (<LinesMesh>m).color = this._coloredMaterial.diffuseColor;
                }
            });
            this._customMeshSet = false;
        }
    }
}
